import * as XLSX from 'xlsx';
import { formatCurrencyFull as formatCurrency, formatPercentage, MONTHS } from '../utils/forecastCalculations';

/**
 * ExportService - Handles Excel export functionality
 * Preserves all existing export logic while organizing it better
 */
export class ExportService {
  constructor() {
    this.YEAR_OPTIONS = [2023, 2024, 2025, 2026];
  }

  /**
   * Returns 'CAPEX', 'OPEX', or '' for a pure single-category amount.
   * Mixed amounts are handled by splitting into separate rows instead.
   */
  getCategoryLabel(capex, opex) {
    if (capex > 0 && opex === 0) return 'CAPEX';
    if (opex > 0 && capex === 0) return 'OPEX';
    return '';
  }

  /**
   * Pro-rates monthly amounts by the given portion/total ratio.
   */
  prorateMonthly(monthlyForecast, portion, total) {
    if (total === 0) return Array(12).fill(0);
    const ratio = portion / total;
    return monthlyForecast.map(m => m * ratio);
  }

  /**
   * Main export function - preserves all existing logic
   */
  exportToExcel(filteredOutcomes, planTotals, planName, year, options = {}) {
    if (filteredOutcomes.length === 0) {
      alert('No outcomes to export');
      return;
    }

    const { portfolioVendorSummary = [], ytdCutoffMonth = -1 } = options;

    // Run environment diagnostics
    this.logEnvironmentInfo(filteredOutcomes, planTotals, planName, year);

    try {
      // Generate export data (preserves all existing logic)
      const exportData = this.generateExportData(filteredOutcomes);
      const summaryData = this.generateSummaryData(filteredOutcomes, planTotals, planName, year);

      // Validate export data before proceeding
      const validation = this.validateExportData(exportData, summaryData);

      if (!validation.exportDataValid || !validation.summaryDataValid) {
        console.error('❌ Export validation failed:', validation);
        alert('Export data validation failed. Please check the console for details.');
        return;
      }

      console.log('✅ Export data validation passed:', validation);

      const vendorData = portfolioVendorSummary.length > 0
        ? this.generateVendorAnalysisSheet(filteredOutcomes, portfolioVendorSummary, ytdCutoffMonth)
        : null;

      this.createAndDownloadWorkbook(exportData, summaryData, planName, year, vendorData);

    } catch (error) {
      console.error('❌ Export failed:', error);
      alert(`Export failed: ${error.message}\n\nPlease check your browser's download settings and try again.`);
    }
  }

  /**
   * Generate main export data - preserves all existing complex logic
   */
  generateExportData(filteredOutcomes) {
    const exportData = [];
    const headers = ['Outcome Name', 'Project Code', 'Company/Party', 'Vendor ID', 'Type', 'Category', 'Details', 'Approved', 'Total', ...MONTHS, 'Total'];
    exportData.push(headers);

    filteredOutcomes.forEach((outcome, outcomeIndex) => {
      // Debug: Log processing of each outcome (limit to first few to avoid spam)
      if (outcomeIndex < 3 || process.env.NODE_ENV === 'development') {
        console.log(`🔍 Processing outcome ${outcomeIndex + 1}/${filteredOutcomes.length}:`, {
          name: outcome.name,
          project_code: outcome.project_code,
          hasOfficialForecast: outcome.hasOfficialForecast,
          hasUploadedSpendData: outcome.hasUploadedSpendData,
          totalProjectedCost: outcome.totalProjectedCost,
          totalActualSpend: outcome.totalActualSpend,
          companyCount: Object.keys(outcome.companyBreakdown || {}).length,
          fixedCostCount: Object.keys(outcome.fixedCostBreakdown || {}).length
        });
      }

      // Add outcome header row
      const outcomeHeaderRow = [
        outcome.name,
        outcome.project_code || 'Not Set',
        `=== ${outcome.name} - OUTCOME SUMMARY ===`,
        '',   // Vendor ID
        'OUTCOME SUMMARY',
        '',   // Category
        '',   // Details
        '',   // Approved
        '', '', '', '', '', '', '', '', '', '', '', '', ''
      ];
      exportData.push(outcomeHeaderRow);

      // Add outcome total rows
      exportData.push(...this.generateOutcomeTotalRows(outcome));
      
      const separatorRow = Array(headers.length).fill('---');
      exportData.push(separatorRow);

      // Add company breakdown
      exportData.push(...this.generateCompanyBreakdownRows(outcome));

      // Add department breakdown
      exportData.push(...this.generateDepartmentBreakdownRows(outcome));

      // Add empty row between outcomes
      const emptyRow = Array(headers.length).fill('');
      exportData.push(emptyRow);
    });

    return exportData;
  }

  /**
   * Generate outcome total rows - preserves existing logic
   */
  generateOutcomeTotalRows(outcome) {
    const rows = [];
    const approvedCapex = outcome.approved_budget?.capex || 0;
    const approvedOpex = outcome.approved_budget?.opex || 0;
    const totalApproved = approvedCapex + approvedOpex;

    // Aggregate projected capex/opex from company breakdown + fixed costs
    const projCapex = Object.values(outcome.companyBreakdown || {}).reduce((s, c) => s + (c.capex || 0), 0)
                    + Object.values(outcome.fixedCostBreakdown || {}).reduce((s, fc) => s + (fc.capex || 0), 0);
    const projOpex  = Object.values(outcome.companyBreakdown || {}).reduce((s, c) => s + (c.opex  || 0), 0)
                    + Object.values(outcome.fixedCostBreakdown || {}).reduce((s, fc) => s + (fc.opex  || 0), 0);
    const projTotal = projCapex + projOpex;

    // 1. Projected Forecast Total — split into CAPEX/OPEX rows if mixed
    const makeProjectedRow = (category, capexAmt, opexAmt, monthly, total) => [
      outcome.name,
      outcome.project_code || 'Not Set',
      'OUTCOME TOTAL',
      '',   // Vendor ID
      'Projected Forecast',
      category,
      '',
      category === 'CAPEX' ? formatCurrency(approvedCapex) : category === 'OPEX' ? formatCurrency(approvedOpex) : formatCurrency(totalApproved),
      formatCurrency(total),
      ...monthly.map(amount => formatCurrency(amount)),
      formatCurrency(total)
    ];

    if (projCapex > 0 && projOpex > 0) {
      rows.push(makeProjectedRow('CAPEX', projCapex, 0, this.prorateMonthly(outcome.monthlyProjected, projCapex, projTotal), projCapex));
      rows.push(makeProjectedRow('OPEX', 0, projOpex, this.prorateMonthly(outcome.monthlyProjected, projOpex, projTotal), projOpex));
    } else {
      rows.push(makeProjectedRow(this.getCategoryLabel(projCapex, projOpex), projCapex, projOpex, outcome.monthlyProjected, outcome.totalProjectedCost));
    }

    // 2. Official Forecast Total (if available)
    if (outcome.hasOfficialForecast) {
      const offCapex = Object.values(outcome.companyOfficialForecast || {}).reduce((s, c) => s + (c.capex || 0), 0);
      const offOpex  = Object.values(outcome.companyOfficialForecast || {}).reduce((s, c) => s + (c.opex  || 0), 0);
      const offTotal = offCapex + offOpex;
      const monthlyOff = outcome.monthlyOfficialForecast.map(a => a !== null ? a : 0);

      const makeOfficialRow = (category, total, monthly) => [
        outcome.name,
        outcome.project_code || 'Not Set',
        'OUTCOME TOTAL',
        '',   // Vendor ID
        'Official Forecast',
        category,
        '',
        '-',
        formatCurrency(total),
        ...monthly.map(amount => formatCurrency(amount)),
        formatCurrency(total)
      ];

      if (offCapex > 0 && offOpex > 0) {
        rows.push(makeOfficialRow('CAPEX', offCapex, this.prorateMonthly(monthlyOff, offCapex, offTotal)));
        rows.push(makeOfficialRow('OPEX', offOpex, this.prorateMonthly(monthlyOff, offOpex, offTotal)));
      } else {
        rows.push(makeOfficialRow(this.getCategoryLabel(offCapex, offOpex), outcome.totalOfficialForecast, monthlyOff));
      }
    }

    // 3. Actuals Total
    const actCapex = Object.values(outcome.companyActuals || {}).reduce((s, c) => s + (c.capex || 0), 0);
    const actOpex  = Object.values(outcome.companyActuals || {}).reduce((s, c) => s + (c.opex  || 0), 0);
    const actTotal = actCapex + actOpex;
    const monthlyAct = outcome.monthlyActual.map(a => a !== null ? a : 0);

    const makeActualsRow = (category, total, monthly) => [
      outcome.name,
      outcome.project_code || 'Not Set',
      'OUTCOME TOTAL',
      '',   // Vendor ID
      'Actuals',
      category,
      '',
      '-',
      formatCurrency(total),
      ...monthly.map(amount => formatCurrency(amount)),
      formatCurrency(total)
    ];

    if (actCapex > 0 && actOpex > 0) {
      rows.push(makeActualsRow('CAPEX', actCapex, this.prorateMonthly(monthlyAct, actCapex, actTotal)));
      rows.push(makeActualsRow('OPEX', actOpex, this.prorateMonthly(monthlyAct, actOpex, actTotal)));
    } else {
      rows.push(makeActualsRow(this.getCategoryLabel(actCapex, actOpex), outcome.totalActualSpend, monthlyAct));
    }

    // 4. Variance Total (no category split — computed delta)
    const varianceTotalRow = [
      outcome.name,
      outcome.project_code || 'Not Set',
      'OUTCOME TOTAL',
      '',   // Vendor ID
      'Actuals vs Projected',
      '',   // Category
      '',
      '-',
      formatCurrency((() => {
        return outcome.monthlyActual.reduce((sum, actual, index) => {
          if (actual !== null) {
            return sum + (actual - outcome.monthlyProjected[index]);
          }
          return sum;
        }, 0);
      })()),
      ...outcome.monthlyActual.map((actual, index) => {
        const projectedForecast = outcome.monthlyProjected[index];
        const variance = actual !== null ? actual - projectedForecast : null;
        return variance !== null ? formatCurrency(variance) : '-';
      }),
      formatCurrency(outcome.totalActualSpend - outcome.totalProjectedCost)
    ];
    rows.push(varianceTotalRow);

    return rows;
  }

  /**
   * Generate company breakdown rows - preserves all existing complex logic
   */
  generateCompanyBreakdownRows(outcome) {
    const rows = [];
    
    // Get all companies
    const allCompanies = new Set();
    if (outcome.companyBreakdown) {
      Object.keys(outcome.companyBreakdown).forEach(company => allCompanies.add(company));
    }
    if (outcome.companyActuals) {
      Object.keys(outcome.companyActuals).forEach(company => allCompanies.add(company));
    }
    if (outcome.companyOfficialForecast) {
      Object.keys(outcome.companyOfficialForecast).forEach(company => allCompanies.add(company));
    }
    if (outcome.fixedCostBreakdown) {
      Object.values(outcome.fixedCostBreakdown).forEach(fixedCost => {
        const company = fixedCost.company || 'CA';
        allCompanies.add(company);
      });
    }
    
    if (allCompanies.size > 0) {
      Array.from(allCompanies).sort().forEach(company => {
        rows.push(...this.generateSingleCompanyRows(outcome, company));
      });
    } else {
      // Fallback for no company data
      const totalApproved = (outcome.approved_budget?.capex || 0) + (outcome.approved_budget?.opex || 0);
      const fallbackRow = [
        outcome.name,
        outcome.project_code || 'Not Set',
        'No Company Data',
        '',   // Vendor ID
        'Projected Forecast',
        '',   // Category
        '',
        formatCurrency(totalApproved),
        formatCurrency(outcome.totalProjectedCost),
        ...outcome.monthlyProjected.map(amount => formatCurrency(amount)),
        formatCurrency(outcome.totalProjectedCost)
      ];
      rows.push(fallbackRow);
    }

    return rows;
  }

  /**
   * Generate department breakdown rows - one row per department using deptBreakdown
   * Fits within the existing 21-column structure:
   *   [name, code, '' (no company), 'DEPT BREAKDOWN', deptName, costCenter, '', total, ...12 monthly, total]
   */
  generateDepartmentBreakdownRows(outcome) {
    const deptBreakdown = outcome.deptBreakdown || {};
    if (Object.keys(deptBreakdown).length === 0) return [];

    const rows = [];

    // Section header row
    const sectionHeader = [
      outcome.name,
      outcome.project_code || 'Not Set',
      `=== DEPARTMENT BREAKDOWN ===`,
      '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
    ];
    rows.push(sectionHeader);

    Object.entries(deptBreakdown).forEach(([dept, breakdown]) => {
      const monthly = breakdown.monthly || Array(12).fill(0);
      const deptTotal = breakdown.total || 0;

      // CAPEX row
      if (breakdown.capex > 0) {
        rows.push([
          outcome.name,
          outcome.project_code || 'Not Set',
          '',
          '',   // Vendor ID
          'DEPT BREAKDOWN',
          dept,
          '—',       // Cost Center placeholder
          'CAPEX',
          formatCurrency(breakdown.capex),
          ...this.prorateMonthly(monthly, breakdown.capex, deptTotal || 1).map(v => formatCurrency(v)),
          formatCurrency(breakdown.capex)
        ]);
      }

      // OPEX row
      if (breakdown.opex > 0) {
        rows.push([
          outcome.name,
          outcome.project_code || 'Not Set',
          '',
          '',   // Vendor ID
          'DEPT BREAKDOWN',
          dept,
          '—',       // Cost Center placeholder
          'OPEX',
          formatCurrency(breakdown.opex),
          ...this.prorateMonthly(monthly, breakdown.opex, deptTotal || 1).map(v => formatCurrency(v)),
          formatCurrency(breakdown.opex)
        ]);
      }

      // Total row when only one category (or as a summary)
      if (breakdown.capex === 0 || breakdown.opex === 0) {
        // Already emitted a single row above; no extra total needed
      } else {
        rows.push([
          outcome.name,
          outcome.project_code || 'Not Set',
          '',
          '',   // Vendor ID
          'DEPT BREAKDOWN',
          dept,
          '—',
          'TOTAL',
          formatCurrency(deptTotal),
          ...monthly.map(v => formatCurrency(v)),
          formatCurrency(deptTotal)
        ]);
      }
    });

    // Separator after dept section
    rows.push(Array(22).fill(''));

    return rows;
  }

  /**
   * Generate rows for a single company - preserves all existing logic
   */
  generateSingleCompanyRows(outcome, company) {
    const rows = [];
    
    // Company Header
    const companyHeaderRow = [
      outcome.name,
      outcome.project_code || 'Not Set',
      `=== ${company} - COMPANY BREAKDOWN ===`,
      '',   // Vendor ID
      '',
      '',   // Category
      '',
      '',
      '', '', '', '', '', '', '', '', '', '', '', '', ''
    ];
    rows.push(companyHeaderRow);
    
    const staffingData = outcome.companyBreakdown?.[company] || { 
      total: 0, capex: 0, opex: 0, 
      monthlyForecast: Array(12).fill(0)
    };
    const officialData = outcome.companyOfficialForecast?.[company] || { 
      total: 0, capex: 0, opex: 0,
      monthlyForecast: Array(12).fill(0)
    };
    const actualData = outcome.companyActuals?.[company] || { total: 0, capex: 0, opex: 0 };
    const approvedData = outcome.companyApprovedBudget?.[company] || { total: 0, capex: 0, opex: 0 };
    
    const companyFixedCosts = Object.entries(outcome.fixedCostBreakdown || {})
      .filter(([_, fixedCost]) => (fixedCost.company || 'CA') === company);
    
    const monthlyActualDistribution = outcome.companyMonthlyActuals?.[company] || Array(12).fill(0);
    
    // 1. Company Projected Forecast (Staffed) — split into CAPEX/OPEX if mixed
    if (staffingData.total > 0) {
      const makeStaffedRow = (category, approved, total, monthly) => [
        outcome.name,
        outcome.project_code || 'Not Set',
        company,
        '',   // Vendor ID
        'Projected Forecast (Staffed)',
        category,
        '',
        approved,
        formatCurrency(total),
        ...monthly.map(amount => formatCurrency(amount)),
        formatCurrency(total)
      ];
      if (staffingData.capex > 0 && staffingData.opex > 0) {
        const sTotal = staffingData.capex + staffingData.opex;
        rows.push(makeStaffedRow('CAPEX', approvedData.capex > 0 ? formatCurrency(approvedData.capex) : '-', staffingData.capex, this.prorateMonthly(staffingData.monthlyForecast, staffingData.capex, sTotal)));
        rows.push(makeStaffedRow('OPEX', approvedData.opex > 0 ? formatCurrency(approvedData.opex) : '-', staffingData.opex, this.prorateMonthly(staffingData.monthlyForecast, staffingData.opex, sTotal)));
      } else {
        rows.push(makeStaffedRow(this.getCategoryLabel(staffingData.capex, staffingData.opex), approvedData.total > 0 ? formatCurrency(approvedData.total) : '-', staffingData.total, staffingData.monthlyForecast));
      }
    }
    
    // 2. Fixed Costs Subtotal and Details
    if (companyFixedCosts.length > 0) {
      rows.push(...this.generateFixedCostRows(outcome, company, companyFixedCosts));
    }
    
    // 3. Company Official Forecast (if available) — split into CAPEX/OPEX if mixed
    if (officialData.total > 0) {
      const forecastLabel = officialData.includesFixedCosts ?
        "Official Forecast (Includes Fixed Costs)" :
        "Official Forecast";
      const offMonthly = officialData.monthlyForecast || Array(12).fill(0);

      const makeOfficialRow = (category, total, monthly) => [
        outcome.name,
        outcome.project_code || 'Not Set',
        company,
        '',   // Vendor ID
        forecastLabel,
        category,
        '',
        '-',
        formatCurrency(total),
        ...monthly.map(amount => amount > 0 ? formatCurrency(amount) : '-'),
        formatCurrency(total)
      ];

      if (officialData.capex > 0 && officialData.opex > 0) {
        const oTotal = officialData.capex + officialData.opex;
        rows.push(makeOfficialRow('CAPEX', officialData.capex, this.prorateMonthly(offMonthly, officialData.capex, oTotal)));
        rows.push(makeOfficialRow('OPEX', officialData.opex, this.prorateMonthly(offMonthly, officialData.opex, oTotal)));
      } else {
        rows.push(makeOfficialRow(this.getCategoryLabel(officialData.capex, officialData.opex), officialData.total, offMonthly));
      }
    }
    
    // 4. Variance Row: Official vs Projected (if both exist)
    if (company === 'CA' && officialData.total > 0) {
      rows.push(...this.generateVarianceRows(outcome, company, staffingData, officialData, companyFixedCosts));
    }
    
    // 5. Company Actual Spend — split into CAPEX/OPEX if mixed, using real monthly arrays when available
    if (actualData.total > 0 || monthlyActualDistribution.some(val => val > 0)) {
      const actualVendorIds = (actualData.vendorIds || []).join(', ');
      const makeActualRow = (category, total, monthly) => [
        outcome.name,
        outcome.project_code || 'Not Set',
        company,
        actualVendorIds,
        'Actual Spend (From Upload)',
        category,
        '',
        '-',
        formatCurrency(total),
        ...monthly.map(amount => amount > 0 ? formatCurrency(amount) : '-'),
        formatCurrency(total)
      ];

      if (actualData.capex > 0 && actualData.opex > 0) {
        // Use real per-month capex/opex arrays if available (Fix 4), otherwise pro-rate
        const aTotal = actualData.capex + actualData.opex;
        const capexMonthly = (actualData.monthlyCapex && actualData.monthlyCapex.some(v => v > 0))
          ? actualData.monthlyCapex
          : this.prorateMonthly(monthlyActualDistribution, actualData.capex, aTotal);
        const opexMonthly = (actualData.monthlyOpex && actualData.monthlyOpex.some(v => v > 0))
          ? actualData.monthlyOpex
          : this.prorateMonthly(monthlyActualDistribution, actualData.opex, aTotal);
        rows.push(makeActualRow('CAPEX', actualData.capex, capexMonthly));
        rows.push(makeActualRow('OPEX', actualData.opex, opexMonthly));
      } else {
        rows.push(makeActualRow(this.getCategoryLabel(actualData.capex, actualData.opex), actualData.total, monthlyActualDistribution));
      }
    }
    
    // Add separator between companies
    const companySeparatorRow = Array(21).fill('');
    rows.push(companySeparatorRow);

    return rows;
  }

  /**
   * Generate fixed cost rows - preserves existing logic
   */
  generateFixedCostRows(outcome, company, companyFixedCosts) {
    const rows = [];

    // Compute subtotal amounts, with per-category monthly tracking
    const totalFixedCapex = companyFixedCosts.reduce((sum, [_, fc]) => sum + (fc.capex || 0), 0);
    const totalFixedOpex  = companyFixedCosts.reduce((sum, [_, fc]) => sum + (fc.opex  || 0), 0);
    const totalFixedCostForCompany = totalFixedCapex + totalFixedOpex;

    // Build monthly totals per category for the subtotal row
    const monthlyFixedCapex = Array(12).fill(0);
    const monthlyFixedOpex  = Array(12).fill(0);
    companyFixedCosts.forEach(([_, fc]) => {
      const fcTotal = (fc.capex || 0) + (fc.opex || 0);
      const capexRatio = fcTotal > 0 ? (fc.capex || 0) / fcTotal : 0;
      fc.monthlyForecast.forEach((amount, i) => {
        monthlyFixedCapex[i] += (amount || 0) * capexRatio;
        monthlyFixedOpex[i]  += (amount || 0) * (1 - capexRatio);
      });
    });
    const monthlyFixedCostForCompany = monthlyFixedCapex.map((c, i) => c + monthlyFixedOpex[i]);

    // Fixed Costs Subtotal — split if mixed
    const makeSubtotalRow = (category, total, monthly) => [
      outcome.name,
      outcome.project_code || 'Not Set',
      company,
      '',   // Vendor ID
      'Fixed Costs Subtotal',
      category,
      '',
      '-',
      formatCurrency(total),
      ...monthly.map(amount => formatCurrency(amount)),
      formatCurrency(total)
    ];

    if (totalFixedCapex > 0 && totalFixedOpex > 0) {
      rows.push(makeSubtotalRow('CAPEX', totalFixedCapex, monthlyFixedCapex));
      rows.push(makeSubtotalRow('OPEX', totalFixedOpex, monthlyFixedOpex));
    } else {
      rows.push(makeSubtotalRow(this.getCategoryLabel(totalFixedCapex, totalFixedOpex), totalFixedCostForCompany, monthlyFixedCostForCompany));
    }

    // Individual Fixed Cost Rows — split if mixed
    companyFixedCosts.forEach(([costName, fixedCost]) => {
      const actualData = outcome.fixedCostActuals?.[costName] || { capex: 0, opex: 0, total: 0 };
      const details = fixedCost.details || '';
      const fcCapex = fixedCost.capex || 0;
      const fcOpex  = fixedCost.opex  || 0;

      const makeFixedRow = (category, total, monthly) => [
        outcome.name,
        outcome.project_code || 'Not Set',
        company,
        '',   // Vendor ID
        `Fixed Cost: ${costName}`,
        category,
        details,
        '-',
        formatCurrency(total),
        ...monthly.map(amount => amount > 0 ? formatCurrency(amount) : '-'),
        formatCurrency(total)
      ];

      if (fcCapex > 0 && fcOpex > 0) {
        const fcTotal = fcCapex + fcOpex;
        rows.push(makeFixedRow('CAPEX', fcCapex, this.prorateMonthly(fixedCost.monthlyForecast, fcCapex, fcTotal)));
        rows.push(makeFixedRow('OPEX', fcOpex, this.prorateMonthly(fixedCost.monthlyForecast, fcOpex, fcTotal)));
      } else {
        rows.push(makeFixedRow(this.getCategoryLabel(fcCapex, fcOpex), fixedCost.total, fixedCost.monthlyForecast));
      }

      if (actualData.total > 0) {
        const fixedActualNote = `Actual: ${formatCurrency(actualData.total)} (${formatCurrency(actualData.total - fixedCost.total)} variance)`;
        const fixedActualRow = [
          outcome.name,
          outcome.project_code || 'Not Set',
          company,
          '',   // Vendor ID
          fixedActualNote,
          '',   // Category
          '',
          '',
          '', '', '', '', '', '', '', '', '', '', '', '', ''
        ];
        rows.push(fixedActualRow);
      }
    });

    return rows;
  }

  /**
   * Generate variance rows - preserves existing logic
   */
  generateVarianceRows(outcome, company, staffingData, officialData, companyFixedCosts) {
    const rows = [];
    
    const companyProjectedTotal = staffingData.total + 
      companyFixedCosts.reduce((sum, [_, fc]) => sum + fc.total, 0);
    
    if (companyProjectedTotal > 0) {
      const totalVariance = officialData.total - companyProjectedTotal;
      const monthlyProjectedTotal = Array(12).fill(0);
      
      // Add staffing costs
      staffingData.monthlyForecast.forEach((amount, index) => {
        monthlyProjectedTotal[index] += amount || 0;
      });
      // Add fixed costs
      companyFixedCosts.forEach(([_, fc]) => {
        fc.monthlyForecast.forEach((amount, index) => {
          monthlyProjectedTotal[index] += amount || 0;
        });
      });
      
      const monthlyVariances = monthlyProjectedTotal.map((projectedAmount, index) => {
        const officialAmount = (officialData.monthlyForecast && officialData.monthlyForecast[index]) || 0;
        return officialAmount - projectedAmount;
      });
      
      const varianceRow = [
        outcome.name,
        outcome.project_code || 'Not Set',
        company,
        '',   // Vendor ID
        'Variance (Official vs Projected+Fixed)',
        '',   // Category — not applicable for variance rows
        '',
        '-',
        formatCurrency(totalVariance),
        ...monthlyVariances.map(variance => variance !== 0 ? formatCurrency(variance) : '-'),
        formatCurrency(totalVariance)
      ];
      rows.push(varianceRow);
    }

    return rows;
  }

  /**
   * Generate summary data - preserves all existing logic
   */
  generateSummaryData(filteredOutcomes, planTotals, planName, year) {
    const summaryData = [];
    const summaryHeaders = ['Category', 'Value', 'CAPEX', 'OPEX', 'Variance vs Approved', 'Variance %', 'Outcome Count', 'Notes'];
    summaryData.push(summaryHeaders);
    
    summaryData.push(['=== EXPORT INFORMATION ===', '', '', '', '', '', '', '']);
    summaryData.push(['Plan Name', planName, '', '', '', '', '', 'Selected plan for analysis']);
    summaryData.push(['Export Date', new Date().toLocaleDateString(), '', '', '', '', '', 'Date when export was generated']);
    summaryData.push(['Export Time', new Date().toLocaleTimeString(), '', '', '', '', '', 'Time when export was generated']);
    summaryData.push(['Year', year.toString(), '', '', '', '', '', 'Analysis year']);
    summaryData.push(['Total Outcomes', filteredOutcomes.length.toString(), '', '', '', '', '', 'Number of outcomes included in analysis']);
    summaryData.push(['']);
    
    // Plan Totals Summary
    summaryData.push(['=== PLAN TOTALS ===', '', '', '', '', '', '', '']);
    
    // Approved Budget
    const approvedVariance = planTotals.totalForecastBudget - planTotals.totalApprovedBudget;
    const approvedVariancePercent = planTotals.totalApprovedBudget > 0 ? 
      (approvedVariance / planTotals.totalApprovedBudget * 100) : 0;
    summaryData.push([
      'Approved Budget', 
      formatCurrency(planTotals.totalApprovedBudget),
      formatCurrency(planTotals.totalApprovedCapex),
      formatCurrency(planTotals.totalApprovedOpex),
      '-',
      '-',
      planTotals.outcomesWithApprovedBudget.toString(),
      'Original approved budget amounts'
    ]);
    
    // Projected Forecast
    summaryData.push([
      'Projected Forecast (Staffing)', 
      formatCurrency(planTotals.totalForecastBudget),
      formatCurrency(planTotals.totalForecastCapex),
      formatCurrency(planTotals.totalForecastOpex),
      formatCurrency(approvedVariance),
      formatPercentage(approvedVariancePercent),
      planTotals.outcomesWithProjectedForecast.toString(),
      'Forecast based on staffing plans and fixed costs'
    ]);
    
    // Official Forecast (if available)
    if (planTotals.totalOfficialForecastBudget > 0) {
      const officialVariance = planTotals.totalOfficialForecastBudget - planTotals.totalApprovedBudget;
      const officialVariancePercent = planTotals.totalApprovedBudget > 0 ? 
        (officialVariance / planTotals.totalApprovedBudget * 100) : 0;
      summaryData.push([
        'Official Forecast (Uploaded)', 
        formatCurrency(planTotals.totalOfficialForecastBudget),
        formatCurrency(planTotals.totalOfficialForecastCapex),
        formatCurrency(planTotals.totalOfficialForecastOpex),
        formatCurrency(officialVariance),
        formatPercentage(officialVariancePercent),
        planTotals.outcomesWithOfficialForecast.toString(),
        'Official forecast from uploaded data'
      ]);
    }
    
    // Actual Spend
    const baselineForecast = planTotals.totalOfficialForecastBudget > 0 ? 
      planTotals.totalOfficialForecastBudget : planTotals.totalForecastBudget;
    const actualVariance = planTotals.totalActualBudget - baselineForecast;
    const actualVariancePercent = baselineForecast > 0 ? 
      (actualVariance / baselineForecast * 100) : 0;
    summaryData.push([
      'Actual Spend', 
      formatCurrency(planTotals.totalActualBudget),
      formatCurrency(planTotals.totalActualCapex),
      formatCurrency(planTotals.totalActualOpex),
      formatCurrency(actualVariance),
      formatPercentage(actualVariancePercent),
      planTotals.outcomesWithActualSpend.toString(),
      'Actual spend from uploaded data'
    ]);
    
    summaryData.push(['']);
    
    // Add more summary sections...
    summaryData.push(...this.generateVarianceAnalysis(planTotals, baselineForecast));
    summaryData.push(...this.generateDataQualityMetrics(filteredOutcomes, planTotals));
    summaryData.push(...this.generateExportMetadata());

    return summaryData;
  }

  /**
   * Generate variance analysis section
   */
  generateVarianceAnalysis(planTotals, baselineForecast) {
    const rows = [];
    
    rows.push(['=== VARIANCE ANALYSIS ===', '', '', '', '', '', '', '']);
    rows.push([
      'Budget Compliance', 
      formatCurrency(planTotals.totalBudgetVariance),
      '-',
      '-',
      formatCurrency(planTotals.totalBudgetVariance),
      planTotals.totalApprovedBudget > 0 ? formatPercentage(planTotals.totalBudgetVariance / planTotals.totalApprovedBudget * 100) : '-',
      planTotals.outcomesOverBudget.toString(),
      'Projected vs Approved Budget'
    ]);
    
    rows.push([
      'Execution Variance', 
      formatCurrency(planTotals.totalExecutionVariance),
      '-',
      '-',
      formatCurrency(planTotals.totalExecutionVariance),
      baselineForecast > 0 ? formatPercentage(planTotals.totalExecutionVariance / baselineForecast * 100) : '-',
      planTotals.outcomesOverTarget.toString(),
      'Actual vs Baseline Forecast'
    ]);
    
    if (planTotals.totalOfficialForecastBudget > 0) {
      rows.push([
        'Forecast Alignment', 
        formatCurrency(planTotals.totalForecastVariance),
        '-',
        '-',
        formatCurrency(planTotals.totalForecastVariance),
        planTotals.totalForecastBudget > 0 ? formatPercentage(planTotals.totalForecastVariance / planTotals.totalForecastBudget * 100) : '-',
        '-',
        'Official vs Projected Forecast'
      ]);
    }
    
    rows.push(['']);
    
    return rows;
  }

  /**
   * Generate data quality metrics section
   */
  generateDataQualityMetrics(filteredOutcomes, planTotals) {
    const rows = [];
    
    rows.push(['=== DATA QUALITY ===', '', '', '', '', '', '', '']);
    const outcomesWithStaffingData = filteredOutcomes.filter(o => o.usingStaffingData).length;
    const outcomesWithCompanyData = filteredOutcomes.filter(o => Object.keys(o.companyBreakdown || {}).length > 0).length;
    const outcomesWithFixedCosts = filteredOutcomes.filter(o => Object.keys(o.fixedCostBreakdown || {}).length > 0).length;
    
    rows.push(['Outcomes with Staffing Data', outcomesWithStaffingData.toString(), '', '', '', '', '', 'Outcomes with detailed staffing plans']);
    rows.push(['Outcomes with Company Breakdown', outcomesWithCompanyData.toString(), '', '', '', '', '', 'Outcomes with company-level cost breakdown']);
    rows.push(['Outcomes with Fixed Costs', outcomesWithFixedCosts.toString(), '', '', '', '', '', 'Outcomes with fixed cost components']);
    rows.push(['Outcomes with Uploaded Spend Data', planTotals.outcomesWithActualSpend.toString(), '', '', '', '', '', 'Outcomes with actual spend data from uploads']);
    rows.push(['Outcomes with Official Forecast', planTotals.outcomesWithOfficialForecast.toString(), '', '', '', '', '', 'Outcomes with official forecast data from uploads']);
    
    rows.push(['']);
    
    return rows;
  }

  /**
   * Generate export metadata section
   */
  generateExportMetadata() {
    const rows = [];
    
    rows.push(['=== EXPORT METADATA ===', '', '', '', '', '', '', '']);
    rows.push(['Export Version', '3.0', '', '', '', '', '', 'Export format version']);
    rows.push(['Data Source', 'PocketPM Forecast vs Actuals', '', '', '', '', '', 'Source system']);
    rows.push(['Generated By', 'System Export', '', '', '', '', '', 'Export method']);
    rows.push(['Currency', 'USD', '', '', '', '', '', 'All amounts in US Dollars']);
    rows.push(['Rounding', 'Nearest Dollar', '', '', '', '', '', 'Currency rounding method']);
    
    return rows;
  }

  /**
   * Create and download workbook - preserves existing logic
   */
  generateVendorAnalysisSheet(filteredOutcomes, portfolioVendorSummary, ytdCutoffMonth) {
    const rows = [];
    const headers = [
      'Vendor', 'Outcome', 'Type', 'Category', 'Total',
      ...MONTHS,
      'EOY'
    ];
    rows.push(headers);

    portfolioVendorSummary.forEach(row => {
      const companyUpper = row.company.toUpperCase();
      const outcomeEntries = filteredOutcomes
        .map(o => {
          const companyKey = Object.keys(o.companyBreakdown || {}).find(k => k.toUpperCase() === companyUpper);
          const actualKey  = Object.keys(o.companyActuals  || {}).find(k => k.toUpperCase() === companyUpper);
          return (companyKey || actualKey)
            ? { o, companyKey: companyKey || row.company, actualKey: actualKey || row.company }
            : null;
        })
        .filter(Boolean);

      // Vendor header
      rows.push([`=== ${row.company} ===`, '', '', '', '', ...Array(12).fill(''), '']);

      // Vendor total rows
      const vendorEoyMonthly = Array(12).fill(0).map((_, i) =>
        ytdCutoffMonth >= 0 && i <= ytdCutoffMonth
          ? (row.monthlyActual[i] || 0)
          : (row.monthlyProjected[i] || 0)
      );
      const vendorEoyTotal = vendorEoyMonthly.reduce((s, v) => s + v, 0);

      rows.push([row.company, 'VENDOR TOTAL', 'Projected', '', formatCurrency(row.projected), ...row.monthlyProjected.map(v => formatCurrency(v || 0)), '']);
      if (row.official > 0) {
        rows.push([row.company, 'VENDOR TOTAL', 'Official Forecast', '', formatCurrency(row.official), ...(row.monthlyOfficial || Array(12).fill(0)).map(v => formatCurrency(v || 0)), '']);
      }
      if (row.actual > 0) {
        rows.push([row.company, 'VENDOR TOTAL', 'YTD Actual', '', formatCurrency(row.actual), ...row.monthlyActual.map(v => formatCurrency(v || 0)), '']);
      }
      rows.push([row.company, 'VENDOR TOTAL', 'EOY Estimate', '', formatCurrency(vendorEoyTotal), ...vendorEoyMonthly.map(v => formatCurrency(v)), formatCurrency(vendorEoyTotal)]);
      rows.push(Array(19).fill(''));

      // Per-outcome rows
      outcomeEntries.forEach(({ o, companyKey, actualKey }) => {
        const staffing = o.companyBreakdown?.[companyKey] || {};
        const actuals  = o.companyActuals?.[actualKey]   || {};
        const monthlyProj = staffing.monthlyForecast || Array(12).fill(0);
        const monthlyAct  = o.companyMonthlyActuals?.[actualKey] || Array(12).fill(0);
        const monthlyCapex = actuals.monthlyCapex || Array(12).fill(0);
        const monthlyOpex  = actuals.monthlyOpex  || Array(12).fill(0);

        if (staffing.total > 0) {
          const capexLabel = staffing.capex > 0 && staffing.opex === 0 ? 'CAPEX' : staffing.opex > 0 && staffing.capex === 0 ? 'OPEX' : '';
          rows.push([row.company, o.name || o.project_code || o.id, 'Projected', capexLabel, formatCurrency(staffing.total), ...monthlyProj.map(v => formatCurrency(v || 0)), '']);
        }

        if (actuals.total > 0) {
          if (actuals.capex > 0 && actuals.opex > 0) {
            rows.push([row.company, o.name || o.project_code || o.id, 'Actual', 'CAPEX', formatCurrency(actuals.capex), ...monthlyCapex.map(v => formatCurrency(v || 0)), '']);
            rows.push([row.company, o.name || o.project_code || o.id, 'Actual', 'OPEX',  formatCurrency(actuals.opex),  ...monthlyOpex.map(v => formatCurrency(v || 0)), '']);
          } else {
            const cat = actuals.capex > 0 ? 'CAPEX' : actuals.opex > 0 ? 'OPEX' : '';
            rows.push([row.company, o.name || o.project_code || o.id, 'Actual', cat, formatCurrency(actuals.total), ...monthlyAct.map(v => formatCurrency(v || 0)), '']);
          }
        }

        // Per-outcome EOY
        let oEoy = 0;
        const oEoyMonthly = Array(12).fill(0).map((_, i) => {
          const v = ytdCutoffMonth >= 0 && i <= ytdCutoffMonth ? (monthlyAct[i] || 0) : (monthlyProj[i] || 0);
          oEoy += v;
          return v;
        });
        if (oEoy > 0) {
          rows.push([row.company, o.name || o.project_code || o.id, 'EOY Estimate', '', formatCurrency(oEoy), ...oEoyMonthly.map(v => formatCurrency(v)), formatCurrency(oEoy)]);
        }
      });

      // Separator between vendors
      rows.push(Array(19).fill(''));
    });

    return rows;
  }

  createAndDownloadWorkbook(exportData, summaryData, planName, year, vendorData = null) {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(exportData);

    const colWidths = [
      { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 30 }, { wch: 10 }, { wch: 30 }, { wch: 15 }, { wch: 15 },
      ...Array(12).fill({ wch: 12 }),
      { wch: 15 }
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Forecast vs Actuals Detail');

    // Summary sheet
    const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
    const summaryColWidths = [
      { wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 40 }
    ];
    summaryWs['!cols'] = summaryColWidths;
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

    // Vendor Analysis sheet (optional)
    if (vendorData) {
      const vendorWs = XLSX.utils.aoa_to_sheet(vendorData);
      vendorWs['!cols'] = [
        { wch: 30 }, { wch: 35 }, { wch: 20 }, { wch: 10 }, { wch: 15 },
        ...Array(12).fill({ wch: 12 }),
        { wch: 15 }
      ];
      XLSX.utils.book_append_sheet(wb, vendorWs, 'Vendor Analysis');
    }

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    const safePlanName = planName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
    const filename = `ForecastVsActuals_${safePlanName}_${year}_${timestamp}.xlsx`;

    try {
      console.log('📁 Attempting to save file:', filename);
      XLSX.writeFile(wb, filename);
      console.log('✅ Export completed successfully:', filename);

      alert(`Export completed successfully!\n\nFile: ${filename}\nOutcomes: ${exportData.length}\nTotal Rows: ${exportData.length + summaryData.length}`);

    } catch (error) {
      console.error('❌ Export failed:', error);
      throw error;
    }
  }

  /**
   * Validate export data - preserves existing validation logic
   */
  validateExportData(exportData, summaryData) {
    const validation = {
      exportDataValid: Array.isArray(exportData) && exportData.length > 0,
      summaryDataValid: Array.isArray(summaryData) && summaryData.length > 0,
      headerRowValid: exportData[0] && exportData[0].length > 0,
      hasOutcomeData: exportData.some(row => row[0] && row[0].includes('===')),
      hasSummaryTotals: summaryData.some(row => row[0] && row[0].includes('PLAN TOTALS')),
      totalRows: exportData.length,
      totalSummaryRows: summaryData.length,
      uniqueOutcomes: new Set(exportData.filter(row => row[0] && row[0].startsWith('===')).map(row => row[0])).size,
      dataIntegrityChecks: {
        allRowsSameLength: exportData.every(row => row.length === exportData[0].length),
        noUndefinedValues: !exportData.some(row => row.some(cell => cell === undefined)),
        noNullValues: !exportData.some(row => row.some(cell => cell === null && typeof cell !== 'string'))
      }
    };
    
    console.log('🔍 Export Data Validation:', validation);
    
    if (!validation.exportDataValid || !validation.summaryDataValid) {
      console.error('❌ Export data validation failed!');
    }
    
    return validation;
  }

  /**
   * Log environment info - preserves existing diagnostic logic
   */
  logEnvironmentInfo(filteredOutcomes, planTotals, planName, year) {
    console.log('🔍 Environment Diagnostic:', {
      nodeEnv: process.env.NODE_ENV,
      apiBaseUrl: process.env.REACT_APP_API_BASE_URL,
      userAgent: navigator.userAgent,
      locale: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      xlsxAvailable: typeof XLSX !== 'undefined',
      xlsxVersion: typeof XLSX !== 'undefined' ? XLSX.version : 'not available',
      xlsxMethods: typeof XLSX !== 'undefined' ? Object.keys(XLSX) : [],
      xlsxUtilsMethods: typeof XLSX !== 'undefined' && XLSX.utils ? Object.keys(XLSX.utils) : [],
      xlsxSource: typeof XLSX !== 'undefined' ? 'loaded' : 'missing',
      downloadCapabilities: {
        blobSupport: typeof Blob !== 'undefined',
        urlCreateObjectURL: typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function',
        documentCreateElement: typeof document !== 'undefined' && typeof document.createElement === 'function'
      },
      currentUrl: window.location.href,
      planName,
      year,
      outcomesCount: filteredOutcomes.length,
      planTotalsSnapshot: {
        approved: planTotals.totalApprovedBudget,
        forecast: planTotals.totalForecastBudget,
        official: planTotals.totalOfficialForecastBudget,
        actual: planTotals.totalActualBudget
      }
    });
  }
}